import { Checkbox } from "@kobalte/core/checkbox";
import { Dialog } from "@kobalte/core/dialog";
import { getIconComponent, ICON_MAPPING, SANCTIONED_ICONS } from "@storytell/bsky-icons";
import { Icons } from "@storytell/cp-client";
import type { Label } from "@storytell/types";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { useNativeTooltipTitle } from "@storytell/ui/tooltip";
import { TbCheck, TbHelpCircle, TbPlus, TbX } from "solid-icons/tb";
import { type Component, createEffect, createMemo, createSignal, For, type Setter, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { DEFAULT_LABEL_COLOR, isLightColor, LABEL_COLOR_SWATCHES } from "../../lib/colors";
import type { LabelListScope } from "./labelScopeFilter";

export interface LabelPickerStrings {
  addLabel?: string;
  searchPlaceholder?: string;
  createNew?: string;
  customizeAppearance?: string;
  clickToChangeColor?: string;
  labelName?: string;
  description?: string;
  searchIcons?: string;
  color?: string;
  icon?: string;
  createLabel?: string;
  learnAboutLabels?: string;
  labelScopeMy?: string;
  labelScopeAll?: string;
  labelScopeAriaLabel?: string;
}

const DEFAULT_STRINGS: Required<LabelPickerStrings> = {
  addLabel: "Add Label",
  searchPlaceholder: "Search for a label",
  createNew: "Create a new Label",
  customizeAppearance: "Customize appearance",
  clickToChangeColor: "Click to change color & icon",
  labelName: "Label Name",
  description: "Description",
  searchIcons: "Search icons...",
  color: "Color",
  icon: "Icon",
  createLabel: "Create Label",
  learnAboutLabels: "Learn about Labels",
  labelScopeMy: "My Labels",
  labelScopeAll: "All Labels",
  labelScopeAriaLabel: "Which labels to show",
};

interface LabelPickerProps {
  mount?: Node;
  search: string;
  setSearch: Setter<string>;
  labels: Label[];
  selectedLabels: string[];
  partialLabels?: string[];
  disableCreate?: boolean;
  title?: string;
  onSelectLabel: (label: Label) => void;
  onDeselectLabel: (label: Label) => void;
  onCreateLabel: (label: { name: string; description: string; icon: Icons.Icon; color: string }) => Promise<void>;
  onClose?: () => void;
  onOpenCreateModal?: () => void;
  canCreateLabels?: boolean;
  strings?: LabelPickerStrings;
  /** Segmented control below search: "My Labels" hides auto-generated style names. */
  showLabelScopeToggle?: boolean;
  labelScope?: LabelListScope;
  onLabelScopeChange?: (scope: LabelListScope) => void;
}

// Helper function to get icon name for search
const getIconName = (icon: Icons.Icon) => {
  const iconKey = Object.keys(Icons.Icon).find((key) => Icons.Icon[key as keyof typeof Icons.Icon] === icon);
  return iconKey?.replace(/_/g, " ").toLowerCase() || "";
};

export const LabelPicker: Component<LabelPickerProps> = (props) => {
  const strings = () => ({ ...DEFAULT_STRINGS, ...props.strings });
  const canCreate = () => props.canCreateLabels ?? true;
  const nativeTitle = useNativeTooltipTitle();

  const [isOpen, setIsOpen] = createSignal(false);
  const [iconSearch, setIconSearch] = createSignal("");
  const [pickerOpen, setPickerOpen] = createSignal(false);
  const [hasSelectedColor, setHasSelectedColor] = createSignal(false);
  const [hasSelectedIcon, setHasSelectedIcon] = createSignal(false);
  const [labelNameInputRef, setLabelNameInputRef] = createSignal<HTMLInputElement | undefined>(undefined);
  const [createLabel, setCreateLabel] = createStore<{
    loading: boolean;
    name: string;
    description: string;
    icon: Icons.Icon;
    color: string;
  }>({
    loading: false,
    name: props.search || "",
    description: "",
    icon: Icons.Icon.FILE,
    color: DEFAULT_LABEL_COLOR,
  });

  // Sync createLabel.name with props.search when modal opens and focus the input
  createEffect(() => {
    if (isOpen()) {
      setCreateLabel("name", props.search || "");
      // Focus the label name input after a short delay to ensure the modal is rendered
      setTimeout(() => {
        const input = labelNameInputRef();
        if (input) {
          input.focus();
          // Select all text if there's a value from search
          if (props.search) {
            input.select();
          }
        }
      }, 100);
    }
  });

  // Reset selection tracking when picker opens
  createEffect(() => {
    if (pickerOpen()) {
      setHasSelectedColor(false);
      setHasSelectedIcon(false);
    }
  });

  // Close picker when both color and icon have been selected
  createEffect(() => {
    if (hasSelectedColor() && hasSelectedIcon() && pickerOpen()) {
      // Use setTimeout to allow the click event to complete before closing
      setTimeout(() => {
        setPickerOpen(false);
      }, 0);
    }
  });

  // Filter icons based on search term
  const filteredIcons = createMemo(() => {
    const searchTerm = iconSearch().toLowerCase().trim();
    if (!searchTerm) return SANCTIONED_ICONS.filter((icon: Icons.Icon) => !!ICON_MAPPING[icon]);

    return SANCTIONED_ICONS.filter((icon: Icons.Icon) => {
      if (!ICON_MAPPING[icon]) return false;
      const iconName = getIconName(icon);

      // First try exact substring match (fastest)
      if (iconName.includes(searchTerm)) return true;

      // Then try flexible search: check if all characters in search term appear in order
      const searchChars = searchTerm.split("");
      let iconIndex = 0;

      for (const char of searchChars) {
        const foundIndex = iconName.indexOf(char, iconIndex);
        if (foundIndex === -1) return false;
        iconIndex = foundIndex + 1;
      }

      return true;
    });
  });

  // Check if search doesn't match any existing labels
  const searchDoesNotMatchLabels = createMemo(() => {
    const searchTerm = props.search.trim().toLowerCase();
    if (!searchTerm) return false;

    return !props.labels.some((label) => label.name.toLowerCase().includes(searchTerm));
  });

  // Handle Enter key press in search input
  const handleSearchKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && searchDoesNotMatchLabels() && !props.disableCreate && canCreate()) {
      e.preventDefault();
      // If onOpenCreateModal is provided, use it (modal is managed externally)
      // Otherwise, use internal modal state
      if (props.onOpenCreateModal) {
        props.onOpenCreateModal();
      } else {
        setIsOpen(true);
        // Close the parent popover after the modal has had a chance to render
        // Use a small delay to ensure the modal state is set and portal can mount before the popover closes
        setTimeout(() => {
          props.onClose?.();
        }, 100);
      }
    }
  };

  return (
    <div class="p-2 pb-3">
      <div class="mb-3 flex items-start justify-between gap-2">
        <h3>{props.title ?? strings().addLabel}</h3>
        <Show when={props.onClose}>
          <StButton icon={TbX} iconClass="size-4" noBorder onClick={props.onClose} class="shrink-0" />
        </Show>
      </div>

      <input
        id="label-search"
        value={props.search}
        onInput={(e) => props.setSearch(e.currentTarget.value)}
        onKeyDown={handleSearchKeyDown}
        type="text"
        placeholder={strings().searchPlaceholder}
        class="mb-3 w-full min-w-52 rounded-lg border border-primary-border p-2 outline-none"
      />

      <Show when={props.showLabelScopeToggle}>
        <div
          class="relative mb-4 flex h-9 w-full rounded-lg border border-primary-border bg-tile p-0.5"
          role="group"
          aria-label={strings().labelScopeAriaLabel}
        >
          <div
            class="pointer-events-none absolute inset-y-0.5 left-0.5 w-[calc(50%-4px)] rounded-md bg-brand shadow-sm transition-transform duration-200 ease-out"
            style={{
              transform: (props.labelScope ?? "my") === "my" ? "translateX(0)" : "translateX(calc(100% + 4px))",
            }}
          />
          <button
            type="button"
            class={twMerge(
              "relative z-10 flex-1 rounded-md text-sm font-medium transition-colors",
              (props.labelScope ?? "my") === "my" ? "text-on-brand" : "text-secondary-text"
            )}
            onClick={() => props.onLabelScopeChange?.("my")}
          >
            {strings().labelScopeMy}
          </button>
          <button
            type="button"
            class={twMerge(
              "relative z-10 flex-1 rounded-md text-sm font-medium transition-colors",
              (props.labelScope ?? "my") === "all" ? "text-on-brand" : "text-secondary-text"
            )}
            onClick={() => props.onLabelScopeChange?.("all")}
          >
            {strings().labelScopeAll}
          </button>
        </div>
      </Show>

      <div class="mb-4 flex max-h-[calc(50vh-14rem)] flex-col space-y-4 overflow-y-auto">
        <For each={props.labels.toSorted((a, b) => a.name.localeCompare(b.name))}>
          {(label) => {
            const isChecked = () => !!props.selectedLabels.find((l) => l === label.id);
            const isPartial = () => !!props.partialLabels?.find((l) => l === label.id);
            return (
              <div class="flex items-center gap-2">
                <Checkbox
                  checked={isChecked()}
                  indeterminate={isPartial()}
                  aria-label={`Select ${label.name}`}
                  onChange={(val) => {
                    if (val) {
                      props.onSelectLabel(label);
                    } else {
                      props.onDeselectLabel(label);
                    }
                  }}
                  class="mr-2 flex items-center justify-center"
                >
                  <Checkbox.Input />
                  <Checkbox.Control
                    data-some-selected={isPartial()}
                    class="flex size-4 items-center justify-center rounded border border-primary-border bg-tile transition-colors data-checked:border-brand data-checked:bg-brand data-[some-selected=true]:bg-black/30 dark:border-white/30 dark:data-[some-selected=true]:bg-white/30"
                  >
                    <Show when={isChecked() || isPartial()}>
                      <Checkbox.Indicator as={TbCheck} class="size-4 stroke-3 text-on-brand" />
                    </Show>
                  </Checkbox.Control>
                </Checkbox>
                <span
                  style={{
                    "--label-color": label.color,
                    "--label-icon": isLightColor(label.color) ? "black" : "white",
                  }}
                >
                  <Dynamic
                    component={getIconComponent(label.icon)}
                    class="size-5 rounded-full bg-(--label-color) p-1 text-(--label-icon)"
                  />
                </span>

                <button
                  type="button"
                  onClick={() => {
                    if (isChecked()) {
                      props.onDeselectLabel(label);
                    } else {
                      props.onSelectLabel(label);
                    }
                  }}
                  class="-mx-1 -my-0.5 line-clamp-2 max-w-60 cursor-pointer rounded px-1 py-0.5 text-left text-sm transition-colors hover:bg-tile-hover"
                >
                  {label.name}
                </button>
              </div>
            );
          }}
        </For>
      </div>

      <Show when={!props.disableCreate}>
        <AnimateChildren show={isOpen()} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
          {(anim) => (
            <Dialog
              open={anim.isMounted()}
              onOpenChange={(val) => {
                if (!val) setIsOpen(false);
              }}
            >
              <Dialog.Portal mount={props.mount}>
                <Dialog.Overlay class="fixed inset-0 z-120 bg-black/50 dark:bg-white/10" />
                <div ref={anim.ref} class="fixed inset-0 z-120 flex items-center justify-center">
                  <Dialog.Content class="relative max-h-[80vh] w-screen max-w-xl overflow-y-auto rounded-xl bg-panel p-8 text-primary-text">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!createLabel.loading && createLabel.name.trim()) {
                          setCreateLabel("loading", true);
                          void props
                            .onCreateLabel({ ...createLabel, name: createLabel.name.trim() || props.search.trim() })
                            .finally(() => setCreateLabel("loading", false));
                        }
                      }}
                    >
                      <div class="mb-4 flex items-start justify-between gap-2">
                        <h3 class="text-lg font-medium">{strings().createNew}</h3>
                        <div class="flex items-center gap-3">
                          <a
                            href="https://go.chief.bot/learn-labels"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex items-center gap-1 text-xs text-brand underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <TbHelpCircle />
                            {strings().learnAboutLabels}
                          </a>
                          <Dialog.CloseButton as={StButton} class="shrink-0">
                            <TbX />
                          </Dialog.CloseButton>
                        </div>
                      </div>

                      <div class="mb-4 flex w-full flex-col gap-3">
                        <div class="flex flex-col gap-2">
                          <StPopover
                            open={pickerOpen()}
                            onOpenChange={setPickerOpen}
                            placement="bottom-start"
                            portalProps={{ mount: props.mount }}
                            contentProps={{ class: "z-[130]" }}
                            content={
                              <div class="grid grid-cols-2 gap-2 p-2">
                                <div>
                                  <p class="mb-2">{strings().color}</p>
                                  <div class="grid max-h-40 grid-cols-5 gap-2 overflow-y-auto p-0.5">
                                    <For each={LABEL_COLOR_SWATCHES.flat()}>
                                      {(color) => (
                                        <button
                                          type="button"
                                          data-active={createLabel.color === color}
                                          aria-label={color}
                                          aria-pressed={createLabel.color === color}
                                          onClick={() => {
                                            setCreateLabel("color", color);
                                            setHasSelectedColor(true);
                                          }}
                                          class="size-7 cursor-pointer rounded-lg data-[active=true]:ring-2 data-[active=true]:ring-primary-text"
                                          style={{ "background-color": color }}
                                        />
                                      )}
                                    </For>
                                  </div>
                                </div>
                                <div>
                                  <p class="mb-2">{strings().icon}</p>
                                  <input
                                    type="text"
                                    placeholder={strings().searchIcons}
                                    value={iconSearch()}
                                    onInput={(e) => setIconSearch(e.currentTarget.value)}
                                    class="mb-2 w-full min-w-0 rounded-lg border border-primary-border p-2 text-sm outline-none"
                                  />
                                  <div class="flex max-h-40 max-w-52 flex-wrap gap-4 overflow-y-auto p-0.5">
                                    <For each={filteredIcons()}>
                                      {(icon: Icons.Icon) => (
                                        <button
                                          type="button"
                                          data-active={createLabel.icon === icon}
                                          onClick={() => {
                                            setCreateLabel("icon", icon);
                                            setHasSelectedIcon(true);
                                          }}
                                          class="grid cursor-pointer place-content-center rounded p-1 text-primary-text transition-colors hover:bg-tile-hover data-[active=true]:ring-2 data-[active=true]:ring-brand"
                                          title={nativeTitle(getIconName(icon))}
                                        >
                                          <Dynamic component={ICON_MAPPING[icon]} class="size-5" />
                                        </button>
                                      )}
                                    </For>
                                  </div>
                                </div>
                              </div>
                            }
                          >
                            <StPopoverTrigger class="flex w-fit cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed border-primary-border p-3 transition-all duration-200 hover:border-brand hover:bg-brand/5">
                              <div
                                class="grid size-10 shrink-0 place-content-center rounded-full shadow-sm ring-2 ring-white dark:ring-gray-900"
                                style={{
                                  "background-color": createLabel.color,
                                  color: isLightColor(createLabel.color) ? "black" : "white",
                                }}
                              >
                                <Dynamic component={ICON_MAPPING[createLabel.icon]} class="size-6" />
                              </div>
                              <div class="flex flex-col items-start">
                                <span class="text-sm font-medium">
                                  <Show when={createLabel.name.trim()} fallback={strings().customizeAppearance}>
                                    {createLabel.name.trim()}
                                  </Show>
                                </span>
                                <span class="text-xs text-secondary-text">
                                  <Show when={createLabel.name.trim()} fallback={strings().clickToChangeColor}>
                                    {createLabel.description.trim()}
                                  </Show>
                                </span>
                              </div>
                            </StPopoverTrigger>
                          </StPopover>
                        </div>

                        <div class="flex flex-col gap-2">
                          <label class="text-sm font-medium text-secondary-text">{strings().labelName}</label>
                          <input
                            ref={setLabelNameInputRef}
                            required
                            type="text"
                            value={createLabel.name}
                            onInput={(e) => setCreateLabel("name", e.currentTarget.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !createLabel.loading && createLabel.name.trim()) {
                                e.preventDefault();
                                setCreateLabel("loading", true);
                                void props
                                  .onCreateLabel({
                                    ...createLabel,
                                    name: createLabel.name.trim() || props.search.trim(),
                                  })
                                  .finally(() => setCreateLabel("loading", false));
                              }
                            }}
                            placeholder={strings().labelName}
                            class="w-full rounded-lg border border-primary-border p-2 outline-none"
                          />
                        </div>
                      </div>
                      <label class="flex flex-col gap-2">
                        <span class="ml-1 text-sm text-secondary-text">{strings().description}</span>
                        <textarea
                          onInput={(e) => setCreateLabel("description", e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" &&
                              (e.ctrlKey || e.metaKey) &&
                              !createLabel.loading &&
                              createLabel.name.trim()
                            ) {
                              e.preventDefault();
                              setCreateLabel("loading", true);
                              void props
                                .onCreateLabel({ ...createLabel, name: createLabel.name.trim() || props.search.trim() })
                                .finally(() => setCreateLabel("loading", false));
                            }
                          }}
                          placeholder={strings().description}
                          class="mb-4 w-full rounded-lg border border-primary-border p-2 outline-none"
                        />
                      </label>

                      <StButton
                        type="submit"
                        loading={createLabel.loading}
                        disabled={createLabel.loading || !createLabel.name.trim()}
                        icon={TbPlus}
                        class="ml-auto bg-brand px-3 py-2 text-on-brand hover:bg-brand-hover"
                      >
                        {strings().createLabel}
                      </StButton>
                    </form>
                  </Dialog.Content>
                </div>
              </Dialog.Portal>
            </Dialog>
          )}
        </AnimateChildren>
        <Show when={canCreate()}>
          <StButton
            icon={TbPlus}
            onClick={() => {
              // If onOpenCreateModal is provided, use it (modal is managed externally)
              // Otherwise, use internal modal state
              if (props.onOpenCreateModal) {
                props.onOpenCreateModal();
              } else {
                setIsOpen(true);
                // Close the parent popover after the modal has had a chance to render
                // Use a small delay to ensure the modal state is set and portal can mount before the popover closes
                setTimeout(() => {
                  props.onClose?.();
                }, 50);
              }
            }}
            iconClass="size-4"
            class={twMerge("w-full", searchDoesNotMatchLabels() && "bg-brand text-on-brand hover:bg-brand-hover")}
          >
            {strings().createNew}
          </StButton>
        </Show>
      </Show>

      <div class="mt-3 flex justify-end">
        <a
          href="https://go.chief.bot/learn-labels"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-xs text-brand underline"
        >
          <TbHelpCircle class="size-4" />
          {strings().learnAboutLabels}
        </a>
      </div>
    </div>
  );
};
